/**
 * SimilarCasesPanel — Muestra casos clínicos semánticamente similares al actual
 * usando el endpoint /cases/similar del backend (pgvector + coseno).
 *
 * Se abre tras completar un caso (o manualmente). Si el backend no responde,
 * el panel simplemente no aparece — no bloquea la experiencia.
 */

import { authService } from '../auth/AuthService';

interface SimilarResult {
  case_id: string;
  case_label: string;
  similarity: number;
}

const PANEL_ID = 'cgt-similar-panel';
const STYLE_ID = 'cgt-similar-styles';

export class SimilarCasesPanel {
  private root: HTMLElement | null = null;

  async showForCase(caseId: string): Promise<void> {
    if (!authService.isAuthenticated) return;

    let results: SimilarResult[];
    try {
      results = await authService.request<SimilarResult[]>('/cases/similar', {
        method: 'POST',
        body: JSON.stringify({ case_id: caseId, limit: 5 }),
      });
    } catch (error) {
      console.warn('[SimilarCases] no se pudo consultar el backend:', error);
      return;
    }
    if (!results.length) return;

    this.render(results);
  }

  async showForQuery(query: string): Promise<void> {
    if (!authService.isAuthenticated) return;
    try {
      const results = await authService.request<SimilarResult[]>('/cases/similar', {
        method: 'POST',
        body: JSON.stringify({ query, limit: 5 }),
      });
      if (results.length) this.render(results);
    } catch (error) {
      console.warn('[SimilarCases] no se pudo consultar el backend:', error);
    }
  }

  close(): void {
    if (!this.root) return;
    const el = this.root;
    el.classList.remove('cgt-sim-visible');
    setTimeout(() => el.remove(), 200);
    this.root = null;
  }

  private render(results: SimilarResult[]): void {
    this.injectStyles();
    this.close();

    const root = document.createElement('div');
    root.id = PANEL_ID;
    root.innerHTML = `
      <header>
        <div>
          <h3>Casos similares</h3>
          <p>Basado en el patrón clínico del caso actual.</p>
        </div>
        <button type="button" data-close aria-label="Cerrar">×</button>
      </header>
      <ul>
        ${results.map(r => `
          <li data-case="${r.case_id}">
            <div class="cgt-sim-label">${this.escape(r.case_label)}</div>
            <div class="cgt-sim-bar">
              <div class="cgt-sim-bar-fill" style="width:${Math.round(r.similarity * 100)}%"></div>
            </div>
            <div class="cgt-sim-pct">${Math.round(r.similarity * 100)}%</div>
          </li>
        `).join('')}
      </ul>
    `;

    root.querySelector('[data-close]')?.addEventListener('click', () => this.close());

    root.querySelectorAll<HTMLElement>('li[data-case]').forEach(li => {
      li.addEventListener('click', () => {
        const id = li.dataset.case;
        if (!id) return;
        const loader = (window as unknown as { app?: { loadCase?: (id: string) => void } }).app;
        if (loader?.loadCase && ['iam', 'sepsis', 'epoc'].includes(id)) {
          loader.loadCase(id as 'iam' | 'sepsis' | 'epoc');
          this.close();
        } else {
          console.info('[SimilarCases] caso no disponible en frontend:', id);
        }
      });
    });

    document.body.appendChild(root);
    requestAnimationFrame(() => root.classList.add('cgt-sim-visible'));
    this.root = root;
  }

  private escape(text: string): string {
    return text.replace(/[&<>"']/g, c => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[c] ?? c));
  }

  private injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      #${PANEL_ID} {
        position: fixed; bottom: 24px; right: 24px;
        z-index: 9000; width: 320px;
        background: rgba(10,10,10,0.92);
        border: 0.5px solid rgba(255,255,255,0.1);
        border-radius: 14px; backdrop-filter: blur(16px);
        font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
        color: #f5f5f7;
        opacity: 0; transform: translateY(12px);
        transition: opacity 0.2s ease, transform 0.2s ease;
      }
      #${PANEL_ID}.cgt-sim-visible { opacity: 1; transform: translateY(0); }
      #${PANEL_ID} header {
        display: flex; align-items: start; justify-content: space-between;
        padding: 14px 16px 8px;
      }
      #${PANEL_ID} h3 { font-size: 13px; font-weight: 700; letter-spacing: -0.2px; }
      #${PANEL_ID} header p { font-size: 11px; color: #6e6e73; margin-top: 2px; }
      #${PANEL_ID} button[data-close] {
        width: 22px; height: 22px; border-radius: 50%;
        background: rgba(255,255,255,0.06); border: none; cursor: pointer;
        color: #a1a1a6; font-size: 14px; line-height: 1;
      }
      #${PANEL_ID} ul { list-style: none; padding: 6px 12px 14px; margin: 0; }
      #${PANEL_ID} li {
        display: grid; grid-template-columns: 1fr 60px 32px;
        align-items: center; gap: 8px;
        padding: 8px 6px; border-radius: 8px;
        cursor: pointer; transition: background 0.15s;
      }
      #${PANEL_ID} li:hover { background: rgba(255,255,255,0.04); }
      #${PANEL_ID} .cgt-sim-label { font-size: 12px; font-weight: 500; color: #f5f5f7; }
      #${PANEL_ID} .cgt-sim-bar {
        height: 4px; background: rgba(255,255,255,0.08);
        border-radius: 2px; overflow: hidden;
      }
      #${PANEL_ID} .cgt-sim-bar-fill {
        height: 100%; background: linear-gradient(90deg, #2997FF, #BF5AF2);
      }
      #${PANEL_ID} .cgt-sim-pct {
        font-family: 'JetBrains Mono', monospace;
        font-size: 10px; color: #a1a1a6; text-align: right;
      }
    `;
    document.head.appendChild(style);
  }
}

export const similarCasesPanel = new SimilarCasesPanel();
